import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from 'react-query';
import { useForm } from 'antd/lib/form/Form';
import { useFormList } from 'hooks/useFormList';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Popconfirm,
} from 'antd';
import {
  addParassetfvvalueuse,
  deleteParassetfvvalueuse,
  getParassetfvvalueuseByContextid,
  ParassetfvvalueuseResponse,
  updateParassetfvvalueuse,
} from 'services/assetconfig';
import DataGrid from 'components/DataGrid';
import ButtonOperation from 'components/ButtonOperation';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { PlusOutlined } from '@ant-design/icons';
import ParListSelect from 'components/ParListSelect';
import SaveButton from 'components/AddButtonSmall';
import DeleteButton from 'components/DeleteButton';
import { indexBy } from 'utils/utils2';
import InputRate from 'components/InputRate';
import { AuthContext } from 'components/Auth/Auth';
import { setValueByNamePath } from 'utils/utils2';

type ParAssetFVDegradationListModalProps = {
  onCancel: () => void;
  visible: boolean;
  fvid: number;
  parcontextid: number;
};
const NEW_ACTION_ID = -1;
const ParAssetFVDegradationListModal = ({
  visible,
  onCancel,
  fvid,
  parcontextid,
}: ParAssetFVDegradationListModalProps) => {
  const { isInspect } = useContext(AuthContext);
  const [form] = useForm();

  const { data: degradation, refetch } = useQuery(
    ['getParassetfvvalueuseByContextid', fvid],
    () =>
      getParassetfvvalueuseByContextid({ fvid: Number(fvid) }).then(
        (res) => res.list || []
      )
  );
  const fieldName = 'degradation';
  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'fvuseid',
    addAsFirst: true,
  });
  const [method, setMethod] = useState('');
  useEffect(() => {
    if (degradation) {
      form.setFieldsValue({
        [fieldName]: degradation,
      });
    }
  }, [degradation, form, refetch]);
  const getName = useCallback(
    (name: string) => (record: ParassetfvvalueuseResponse) => {
      const actions = form.getFieldValue(fieldName);
      const index = indexBy(actions, 'fvuseid')[record?.fvuseid];
      return [fieldName, index, name];
    },
    [form]
  );

  const onSave = useCallback(
    (record: ParassetfvvalueuseResponse) => {
      const condition: ParassetfvvalueuseResponse[] =
        form.getFieldValue(fieldName);
      const index = indexBy(condition, 'fvuseid')[record.fvuseid];
      const currentValue = {
        ...condition[index],
      };
      const { fvuseid } = currentValue;

      if (fvuseid === NEW_ACTION_ID) {
        addParassetfvvalueuse({
          request: { ...currentValue, fvuseid: null as any },
        }).then((response) => {
          if (response.code === '200') {
            notification.success({ message: response.message });
          }
          refetch();
        });
      } else {
        return updateParassetfvvalueuse({
          fvuseid: Number(fvuseid),
          request: currentValue,
        }).then((response) => {
          if (response.code === '200') {
            notification.success({
              message: response.message,
            });
            refetch();
          } else {
            notification.error({
              message: response.message,
            });
          }
        });
      }
    },
    [form, refetch]
  );

  const columns = useMemo<DataGridColDef<ParassetfvvalueuseResponse>[]>(
    () => [
      {
        dataIndex: 'fvuseid',
        render: (value, record, index) => {
          return record?.fvuseid === NEW_ACTION_ID ? (
            <Form.Item
              noStyle
              name={getName('fvuseid')(record)}
              rules={[{ required: true }]}
            >
              <Input disabled />
            </Form.Item>
          ) : (
            <Input value={value} />
          );
        },
        width: '60px',
        fixed: true,
      },
      {
        dataIndex: 'attcode',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('attcode')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect parcode="ASSETUSE" />
          </Form.Item>
        ),
        width: '110px',
      },

      {
        dataIndex: 'usevaluemin',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('usevaluemin')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: '100px',
      },
      {
        dataIndex: 'usevaluemax',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('usevaluemax')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: '100px',
      },
      {
        dataIndex: 'usedegradationmethod',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('usedegradationmethod')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect
              parcode="USEDEGMETH"
              onSelect={(e) => {
                setMethod(e);
                setValueByNamePath(
                  form,
                  getName('degradationcoef')(record),
                  null
                );
                setValueByNamePath(
                  form,
                  getName('degradationfixedamount')(record),
                  null
                );
              }}
            />
          </Form.Item>
        ),
        width: '110px',
      },
      {
        dataIndex: 'degradationcoef',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('degradationcoef')(record)}
            rules={[{ required: true }]}
          >
            <InputRate
              style={{ width: '100%' }}
              disabled={method === 'AMOUNT'}
            />
          </Form.Item>
        ),
        width: '110px',
      },
      {
        dataIndex: 'degradationfixedamount',

        render: (value, record) => (
          <Form.Item
            noStyle
            name={getName('degradationfixedamount')(record)}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} disabled={method === 'COEF'} />
          </Form.Item>
        ),
        width: '110px',
      },

      {
        dataIndex: 'Actions',
        render: (_, record, index) =>
          record?.fvuseid === NEW_ACTION_ID ? (
            <SaveButton
              comcode="save"
              size="small"
              onClick={() => !isInspect && onSave(record)}
              htmlType="button"
            />
          ) : (
            <>
              <SaveButton
                comcode="update"
                size="small"
                onClick={() => !isInspect && onSave(record)}
                htmlType="button"
              />

              <Popconfirm
                title="Are you sure to delete this Condition?"
                onConfirm={() => {
                  deleteParassetfvvalueuse({
                    fvuseid: record.fvuseid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </>
          ),
        width: '110px',
        fixed: 'left',
      },
    ],

    [getName, onSave, refetch, remove, method, isInspect]
  );
  const addRow = () => {
    const row: ParassetfvvalueuseResponse = {
      fvid: fvid,
      attcode: '',
      degradationcoef: 0,
      degradationfixedamount: 0,
      fvuseid: NEW_ACTION_ID,
      parcontextid: parcontextid,
      usedegradationmethod: '',
      usevaluemax: 0,
      usevaluemin: 0,
    };
    add(row);
  };
  return (
    (<Modal
      open={visible}
      onCancel={onCancel}
      width={1400}
      className="fitcontent"
      footer={null}
    >
      <Card type="inner">
        <Form form={form}>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const degradation: ParassetfvvalueuseResponse[] =
                getFieldValue(fieldName);
              const isNewItemInTable =
                degradation?.find(
                  ({ fvuseid }) => fvuseid === NEW_ACTION_ID
                ) !== undefined;
              return (
                <DataGrid
                  titleComcode="ParAssetFutureValueUseList"
                  rowKey="fvuseid"
                  columns={columns}
                  dataSource={degradation}
                  hideFilters
                  extraActions={
                    <ButtonOperation
                      comcode="Add"
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => !isInspect && addRow()}
                      disabled={isNewItemInTable}
                    />
                  }
                />
              );
            }}
          </Form.Item>
        </Form>
      </Card>
    </Modal>)
  );
};
export default ParAssetFVDegradationListModal;
